import type {
  BusinessCenterPermissionDefinition,
  BusinessCenterPermissionKey,
  BusinessCenterPermissionMatrix,
  BusinessCenterRoleDefinition,
  BusinessCenterRoleKey,
  BusinessCenterRolesWorkspace,
  BusinessCenterUserRoleAssignment,
} from "../../lib/types";
import { createBusinessCenterOperationalStateStore } from "../../lib/businessCenterOperationalStateStore";

export const businessCenterRoleDefinitions: BusinessCenterRoleDefinition[] = [
  {
    key: "admin",
    label: "Admin",
    description: "Full workspace oversight with broad operational access.",
    accessLevel: 100,
  },
  {
    key: "manager",
    label: "Manager",
    description: "Can manage delivery, clients, projects, and reporting surfaces.",
    accessLevel: 80,
  },
  {
    key: "team_member",
    label: "Team Member",
    description: "Can work tasks, documents, and project activity within assigned scope.",
    accessLevel: 60,
  },
  {
    key: "client",
    label: "Client",
    description: "Can view portal data and assigned client-facing records.",
    accessLevel: 35,
  },
  {
    key: "viewer",
    label: "Viewer",
    description: "Read-only visibility for internal oversight and review.",
    accessLevel: 10,
  },
];

export const businessCenterPermissionDefinitions: BusinessCenterPermissionDefinition[] = [
  { key: "dashboard.view", label: "View dashboard", description: "See Business Center summary and KPI surfaces.", group: "Overview" },
  { key: "leads.view", label: "View leads", description: "Inspect CRM and lead records.", group: "CRM" },
  { key: "leads.edit", label: "Edit leads", description: "Create and update lead records.", group: "CRM" },
  { key: "clients.view", label: "View clients", description: "Inspect client records and history.", group: "Records" },
  { key: "clients.edit", label: "Edit clients", description: "Create and update client records.", group: "Records" },
  { key: "projects.view", label: "View projects", description: "Inspect project delivery records.", group: "Delivery" },
  { key: "projects.edit", label: "Edit projects", description: "Create and update project records.", group: "Delivery" },
  { key: "tasks.view", label: "View tasks", description: "Inspect tasks and work queues.", group: "Delivery" },
  { key: "tasks.edit", label: "Edit tasks", description: "Create and update task records.", group: "Delivery" },
  { key: "documents.view", label: "View documents", description: "Inspect document records and folder structure.", group: "Documents" },
  { key: "documents.edit", label: "Edit documents", description: "Create and update document records.", group: "Documents" },
  { key: "billing.view", label: "View billing", description: "Inspect invoices and payment history.", group: "Billing" },
  { key: "billing.edit", label: "Edit billing", description: "Create and update billing records.", group: "Billing" },
  { key: "reports.view", label: "View reports", description: "Inspect reporting and analytics surfaces.", group: "Reporting" },
  { key: "reports.export", label: "Export reports", description: "Export reporting summaries.", group: "Reporting" },
  { key: "portal.view", label: "View portal", description: "Access client portal foundation screens.", group: "Portal" },
  { key: "roles.view", label: "View roles", description: "Inspect role definitions and permission matrix.", group: "Security" },
  { key: "roles.edit", label: "Edit roles", description: "Change role mappings and assignments.", group: "Security" },
];

export const businessCenterPermissionMatrixDefaults: BusinessCenterPermissionMatrix = {
  admin: {
    "dashboard.view": true,
    "leads.view": true,
    "leads.edit": true,
    "clients.view": true,
    "clients.edit": true,
    "projects.view": true,
    "projects.edit": true,
    "tasks.view": true,
    "tasks.edit": true,
    "documents.view": true,
    "documents.edit": true,
    "billing.view": true,
    "billing.edit": true,
    "reports.view": true,
    "reports.export": true,
    "portal.view": true,
    "roles.view": true,
    "roles.edit": true,
  },
  manager: {
    "dashboard.view": true,
    "leads.view": true,
    "leads.edit": true,
    "clients.view": true,
    "clients.edit": true,
    "projects.view": true,
    "projects.edit": true,
    "tasks.view": true,
    "tasks.edit": true,
    "documents.view": true,
    "documents.edit": true,
    "billing.view": true,
    "billing.edit": false,
    "reports.view": true,
    "reports.export": true,
    "portal.view": true,
    "roles.view": true,
    "roles.edit": false,
  },
  supervisor: {
    "dashboard.view": true,
    "leads.view": false,
    "leads.edit": false,
    "clients.view": true,
    "clients.edit": true,
    "projects.view": true,
    "projects.edit": true,
    "tasks.view": true,
    "tasks.edit": true,
    "documents.view": true,
    "documents.edit": true,
    "billing.view": false,
    "billing.edit": false,
    "reports.view": true,
    "reports.export": true,
    "portal.view": false,
    "roles.view": true,
    "roles.edit": false,
  },
  team_member: {
    "dashboard.view": true,
    "leads.view": false,
    "leads.edit": false,
    "clients.view": true,
    "clients.edit": false,
    "projects.view": true,
    "projects.edit": false,
    "tasks.view": true,
    "tasks.edit": true,
    "documents.view": true,
    "documents.edit": true,
    "billing.view": false,
    "billing.edit": false,
    "reports.view": true,
    "reports.export": false,
    "portal.view": false,
    "roles.view": false,
    "roles.edit": false,
  },
  client: {
    "dashboard.view": true,
    "leads.view": false,
    "leads.edit": false,
    "clients.view": true,
    "clients.edit": false,
    "projects.view": true,
    "projects.edit": false,
    "tasks.view": true,
    "tasks.edit": false,
    "documents.view": true,
    "documents.edit": false,
    "billing.view": true,
    "billing.edit": false,
    "reports.view": true,
    "reports.export": false,
    "portal.view": true,
    "roles.view": false,
    "roles.edit": false,
  },
  viewer: {
    "dashboard.view": true,
    "leads.view": false,
    "leads.edit": false,
    "clients.view": true,
    "clients.edit": false,
    "projects.view": true,
    "projects.edit": false,
    "tasks.view": true,
    "tasks.edit": false,
    "documents.view": true,
    "documents.edit": false,
    "billing.view": false,
    "billing.edit": false,
    "reports.view": true,
    "reports.export": false,
    "portal.view": true,
    "roles.view": true,
    "roles.edit": false,
  },
};

const roleAssignmentsStore = createBusinessCenterOperationalStateStore<BusinessCenterUserRoleAssignment[]>({
  moduleKey: "business_center_role_assignments",
  scope: { scopeKey: "roles-center", scopeType: "platform" },
  seed: createSeedRoleAssignments,
  normalize: (assignments) => assignments.map(normalizeAssignment),
  isEmpty: (assignments) => !Array.isArray(assignments) || assignments.length === 0,
});

export function loadBusinessCenterRoleAssignments(): BusinessCenterUserRoleAssignment[] {
  return roleAssignmentsStore.read();
}

export async function refreshBusinessCenterRoleAssignments() {
  return roleAssignmentsStore.refresh();
}

export function loadBusinessCenterRolesWorkspace(): BusinessCenterRolesWorkspace {
  return {
    roles: businessCenterRoleDefinitions,
    assignments: loadBusinessCenterRoleAssignments(),
    permissionMatrix: businessCenterPermissionMatrixDefaults,
  };
}

export function updateBusinessCenterUserRoleAssignment(userId: string, role: BusinessCenterRoleKey) {
  const current = loadBusinessCenterRoleAssignments();
  const updated = current.map((assignment) =>
    assignment.id === userId
      ? normalizeAssignment({
          ...assignment,
          role,
          updatedAt: new Date().toISOString(),
        })
      : assignment,
  );
  saveBusinessCenterRoleAssignments(updated);
  return updated;
}

export function upsertBusinessCenterUserRoleAssignment(nextAssignment: BusinessCenterUserRoleAssignment) {
  const current = loadBusinessCenterRoleAssignments();
  const normalized = normalizeAssignment({ ...nextAssignment, updatedAt: new Date().toISOString() });
  const exists = current.some((assignment) => assignment.id === normalized.id);
  const assignments = exists
    ? current.map((assignment) => (assignment.id === normalized.id ? normalized : assignment))
    : [normalized, ...current];
  saveBusinessCenterRoleAssignments(assignments);
  return assignments;
}

function saveBusinessCenterRoleAssignments(assignments: BusinessCenterUserRoleAssignment[]) {
  roleAssignmentsStore.write(assignments);
}

function normalizeAssignment(assignment: BusinessCenterUserRoleAssignment): BusinessCenterUserRoleAssignment {
  const roles: BusinessCenterRoleKey[] = ["admin", "manager", "team_member", "client", "viewer"];
  return {
    ...assignment,
    role: roles.includes(assignment.role) ? assignment.role : "viewer",
    recordType: assignment.recordType === "client" ? "client" : "internal",
    notes: assignment.notes || "",
    createdAt: assignment.createdAt || new Date().toISOString(),
    updatedAt: assignment.updatedAt || new Date().toISOString(),
  };
}

function createSeedRoleAssignments(): BusinessCenterUserRoleAssignment[] {
  const now = new Date().toISOString();
  return [
    {
      id: crypto.randomUUID(),
      userName: "Kreatethedate Admin",
      userEmail: "admin@kreatethedate.com",
      role: "admin",
      recordType: "internal",
      notes: "Primary admin workspace access.",
      createdAt: now,
      updatedAt: now,
    },
    {
      id: crypto.randomUUID(),
      userName: "Operations Manager",
      userEmail: "manager@truevanceconnect.com",
      role: "manager",
      recordType: "internal",
      notes: "Manages delivery and reporting surfaces.",
      createdAt: now,
      updatedAt: now,
    },
    {
      id: crypto.randomUUID(),
      userName: "Project Team Member",
      userEmail: "team@truevanceconnect.com",
      role: "team_member",
      recordType: "internal",
      notes: "Handles tasks and document updates within scope.",
      createdAt: now,
      updatedAt: now,
    },
    {
      id: crypto.randomUUID(),
      userName: "Client Primary Contact",
      userEmail: "client@example.com",
      role: "client",
      recordType: "client",
      notes: "Representative portal access for client review.",
      createdAt: now,
      updatedAt: now,
    },
    {
      id: crypto.randomUUID(),
      userName: "Read Only Reviewer",
      userEmail: "viewer@example.com",
      role: "viewer",
      recordType: "internal",
      notes: "Read only access for oversight and approvals.",
      createdAt: now,
      updatedAt: now,
    },
  ];
}

export function countPermissionsForRole(
  matrix: BusinessCenterPermissionMatrix,
  role: BusinessCenterRoleKey,
): number {
  return (Object.keys(matrix[role]) as BusinessCenterPermissionKey[]).filter((key) => matrix[role][key]).length;
}
